/**
 * Offline imitation-spike search: reference blend weights + shape scalars.
 *
 * Lexicographic score (v1):
 *   1) Gate A hard gates (unchanged thresholds) — first key
 *   2) soft: mean joint tracking error + COM progress match + slip/sat residuals
 *
 * Lexicographic score (v2 demo style — NOT certification):
 *   1) upright completion hard: full cycle + final both contacts + h≥0.9 +
 *      tilt≤15 + zero assists
 *   2) style soft: knee-led lift, arms-down achieved, smoothness, tracking
 * Gate A hard gates are recorded for information only on v2.
 *
 * All-zero blend is the identity baseline (no feedforward).
 */
import type { LocomotionGateATrialMetrics } from './humanoidMuscleEvaluation';
import {
  LOCOMOTION_GATE_A_THRESHOLDS,
  evaluateGateATrialHardGates,
} from './humanoidMuscleEvaluation';
import type { P1ATrialStyleKnobs } from './humanoidMuscleController';
import {
  ZERO_REFERENCE_STEP_BLEND,
  type ReferenceStepBlendWeights,
  type ReferenceStepShapeScalars,
} from './humanoidReferenceStep';

/** Deterministic mulberry32 for candidate sampling. */
export function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export interface ImitationCandidate {
  readonly index: number;
  readonly blend: ReferenceStepBlendWeights;
  readonly shape: Required<ReferenceStepShapeScalars>;
  /** Style knobs plumbed into the trial (includes blend + shape). */
  readonly style: P1ATrialStyleKnobs;
}

export interface ImitationScore {
  /** 0 = all hard gates pass; 1 = any hard gate fail. Lex first key. */
  readonly hardFail: 0 | 1;
  /** Soft cost (only ranks when hardFail equal). Lower is better. */
  readonly soft: number;
  readonly hardReason: string | null;
  readonly meanJointTrackingErrorRad: number | null;
  readonly comProgressMatchErrorM: number;
  readonly newFootSlipM: number;
  readonly continuousSaturationMaxS: number | null;
}

/** Soft weights for the imitation search (documentation of intent). */
export const IMITATION_SOFT_W = {
  trackingRad: 2.0,
  comProgressM: 8.0,
  newFootSlipM: 40.0,
  contSatS: 12.0,
  stanceSlipM: 20.0,
  landingErrorM: 5.0,
} as const;

/**
 * Lexicographic Gate A hard gates FIRST (unchanged thresholds), then soft
 * tracking + residual cost.
 */
export function scoreImitationTrial(
  m: LocomotionGateATrialMetrics,
  th = LOCOMOTION_GATE_A_THRESHOLDS,
): ImitationScore {
  const hardReason = evaluateGateATrialHardGates(m, th);
  const hardFail: 0 | 1 = hardReason === null ? 0 : 1;
  const track = m.meanJointTrackingErrorRad;
  const comErr = m.comProgressMatchErrorM;
  const contSat = m.continuousSaturationMaxS;
  let soft = 0;
  soft += (track ?? 0.5) * IMITATION_SOFT_W.trackingRad;
  soft += comErr * IMITATION_SOFT_W.comProgressM;
  soft += m.newFootSlipM * IMITATION_SOFT_W.newFootSlipM;
  soft += (contSat ?? 2) * IMITATION_SOFT_W.contSatS;
  soft += m.stanceSlipM * IMITATION_SOFT_W.stanceSlipM;
  soft += m.landingErrorM * IMITATION_SOFT_W.landingErrorM;
  // Incomplete / abort: must rank worse than a full-cycle baseline that slips.
  // (Zero-slip prepare-abort must never beat a completed step on soft alone.)
  if (m.prepareAborted) soft += 200;
  if (!m.sawLift) soft += 120;
  if (!m.sawPlace) soft += 80;
  if (!m.sawLoad) soft += 60;
  if (!m.doubleSingleDouble) soft += 50;
  // Height/tilt hinges: collapse must rank far below upright incomplete steps.
  if (m.minPelvisHeightM < th.minPelvisHeightM) {
    soft += 80 + (th.minPelvisHeightM - m.minPelvisHeightM) * 200;
  }
  if (m.maxPelvisTiltDeg > th.maxPelvisTiltDeg) {
    soft += 40 + (m.maxPelvisTiltDeg - th.maxPelvisTiltDeg) * 2;
  }
  return {
    hardFail,
    soft,
    hardReason,
    meanJointTrackingErrorRad: track,
    comProgressMatchErrorM: comErr,
    newFootSlipM: m.newFootSlipM,
    continuousSaturationMaxS: contSat,
  };
}

export function compareImitationScores(a: ImitationScore, b: ImitationScore): number {
  if (a.hardFail !== b.hardFail) return a.hardFail - b.hardFail;
  return a.soft - b.soft;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function mapUnit(u: number, min: number, max: number): number {
  return min + clamp01(u) * (max - min);
}

/**
 * Build a physical candidate from a unit hypercube sample in [0,1]^dims.
 * Dim layout (9):
 *   0–5 blend: prepare, lift, swing, land, load, settle
 *     prepare ≤0.35 (high prepare FF aborts load-transfer), others ≤0.55
 *   6–8 shape: swingFlex, stanceBrace, spine in [0.7, 1.3]
 * Index 0 reserved for identity (all-zero blend).
 */
export function unitToImitationCandidate(
  index: number,
  unit: readonly number[],
): ImitationCandidate {
  if (index === 0) {
    const blend = { ...ZERO_REFERENCE_STEP_BLEND };
    const shape = {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    };
    return {
      index,
      blend,
      shape,
      style: {},
    };
  }
  const u = (i: number, def = 0.5) =>
    typeof unit[i] === 'number' && Number.isFinite(unit[i]!) ? unit[i]! : def;
  const blend: ReferenceStepBlendWeights = {
    prepare: mapUnit(u(0), 0, 0.35),
    lift: mapUnit(u(1), 0, 0.55),
    swing: mapUnit(u(2), 0, 0.55),
    land: mapUnit(u(3), 0, 0.55),
    load: mapUnit(u(4), 0, 0.55),
    settle: mapUnit(u(5), 0, 0.35),
  };
  const shape = {
    swingFlexScale: mapUnit(u(6), 0.7, 1.3),
    stanceBraceScale: mapUnit(u(7), 0.7, 1.3),
    spineScale: mapUnit(u(8), 0.7, 1.3),
    armSwingScale: 1,
  };
  const style: P1ATrialStyleKnobs = {
    referenceBlend: blend,
    referenceShape: shape,
  };
  return { index, blend, shape, style };
}

/**
 * Structured low-risk blend presets (prepare=0 to protect load-transfer).
 * Inserted after identity so smoke/full always explore land/load FF.
 */
export const IMITATION_STRUCTURED_PRESETS: readonly {
  blend: ReferenceStepBlendWeights;
  shape: Required<ReferenceStepShapeScalars>;
}[] = Object.freeze([
  {
    blend: {
      prepare: 0,
      lift: 0.15,
      swing: 0.2,
      land: 0.25,
      load: 0.3,
      settle: 0.1,
    },
    shape: {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    },
  },
  {
    blend: {
      prepare: 0,
      lift: 0,
      swing: 0.1,
      land: 0.35,
      load: 0.4,
      settle: 0.15,
    },
    shape: {
      swingFlexScale: 1.05,
      stanceBraceScale: 0.95,
      spineScale: 1,
      armSwingScale: 1,
    },
  },
  {
    blend: {
      prepare: 0,
      lift: 0.1,
      swing: 0.15,
      land: 0.15,
      load: 0.5,
      settle: 0.2,
    },
    shape: {
      swingFlexScale: 0.9,
      stanceBraceScale: 1.1,
      spineScale: 0.9,
      armSwingScale: 1,
    },
  },
  {
    blend: {
      prepare: 0,
      lift: 0.25,
      swing: 0.3,
      land: 0.2,
      load: 0.2,
      settle: 0,
    },
    shape: {
      swingFlexScale: 1.15,
      stanceBraceScale: 1,
      spineScale: 1.05,
      armSwingScale: 1,
    },
  },
  {
    blend: {
      prepare: 0.05,
      lift: 0.05,
      swing: 0.05,
      land: 0.1,
      load: 0.15,
      settle: 0.05,
    },
    shape: {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    },
  },
]);

/** Deterministic batch of N candidates; index 0 is always identity. */
export function sampleImitationCandidates(
  count: number,
  seed: number,
): ImitationCandidate[] {
  const n = Math.max(1, Math.floor(count));
  const rng = mulberry32(seed);
  const out: ImitationCandidate[] = [];
  out.push(unitToImitationCandidate(0, []));
  // Structured presets next.
  for (let p = 0; p < IMITATION_STRUCTURED_PRESETS.length && out.length < n; p += 1) {
    const preset = IMITATION_STRUCTURED_PRESETS[p]!;
    const index = out.length;
    out.push({
      index,
      blend: preset.blend,
      shape: preset.shape,
      style: {
        referenceBlend: preset.blend,
        referenceShape: preset.shape,
      },
    });
  }
  while (out.length < n) {
    const unit = Array.from({ length: 9 }, () => rng());
    // Force prepare unit into lower half of its already-capped range.
    unit[0] = (unit[0] ?? 0.5) * 0.5;
    out.push(unitToImitationCandidate(out.length, unit));
  }
  return out;
}

/**
 * Short P1B-style gates for 0.10 m steps (clearance 0.03–0.10 post-amendment).
 * Does NOT relax Gate A contSat — reports both.
 */
export function scoreP1BShortGates(m: LocomotionGateATrialMetrics): string | null {
  if (m.forbiddenAssistCount > 0 || m.forbiddenWorldApiCallsDuringControl > 0) {
    return 'forbidden assists';
  }
  if (!m.sawPrepare || !m.sawLift || !m.sawPlace || !m.sawLoad) {
    return 'incomplete phases';
  }
  if (!m.doubleSingleDouble) return 'no double→single→double';
  if (m.swingEndBeginCycles !== 1) return `cycles=${m.swingEndBeginCycles}`;
  if (m.stanceSlipM > 0.03) return `stance slip ${m.stanceSlipM.toFixed(4)}`;
  if (m.peakClearanceM < 0.03 || m.peakClearanceM > 0.1) {
    return `clearance ${m.peakClearanceM.toFixed(4)}`;
  }
  if (m.landingErrorM > 0.08) return `landing ${m.landingErrorM.toFixed(4)}`;
  if (m.footAdvanceM < 0.06 || m.footAdvanceM > 0.16) {
    return `foot advance ${m.footAdvanceM.toFixed(4)}`;
  }
  if (m.minPelvisHeightM < 0.8) return `height ${m.minPelvisHeightM.toFixed(3)}`;
  if (m.maxPelvisTiltDeg > 25) return `tilt ${m.maxPelvisTiltDeg.toFixed(2)}`;
  return null;
}

export function scoreP1CGates(m: LocomotionGateATrialMetrics): string | null {
  if (m.newFootSlipM > 0.04) return `new-foot slip ${m.newFootSlipM.toFixed(4)}`;
  if (!m.finalStabilityWindowComplete) return 'final stability incomplete';
  if (
    m.finalComSpeedMaxMps === null ||
    m.finalTiltMaxDeg === null ||
    m.finalComSpeedMaxMps > 0.08 ||
    m.finalTiltMaxDeg > 8 ||
    !m.finalBothContacts ||
    m.finalSupportMarginMinM < 0.01
  ) {
    return 'final 1s stability failed';
  }
  return null;
}

export interface ImitationGateTable {
  readonly forwardM: number;
  readonly seed: number;
  readonly hardPass: boolean;
  readonly hardReason: string | null;
  readonly p1bShortFail: string | null;
  readonly p1cFail: string | null;
  readonly footAdvanceM: number;
  readonly landingErrorM: number;
  readonly peakClearanceM: number;
  readonly stanceSlipM: number;
  readonly newFootSlipM: number;
  readonly pelvisAdvanceM: number;
  readonly comAdvanceM: number;
  readonly minPelvisHeightM: number;
  readonly maxPelvisTiltDeg: number;
  readonly continuousSaturationMaxS: number | null;
  readonly motorSaturationFraction: number | null;
  readonly meanJointTrackingErrorRad: number | null;
  readonly comProgressMatchErrorM: number;
  readonly doubleSingleDouble: boolean;
  readonly finalStabilityWindowComplete: boolean;
}

export function gateTableFromMetrics(
  m: LocomotionGateATrialMetrics,
  forwardM: number,
): ImitationGateTable {
  const hardReason = evaluateGateATrialHardGates(m, LOCOMOTION_GATE_A_THRESHOLDS);
  // For short steps, Gate A foot-advance band is wrong — report P1B short gates.
  const p1b =
    forwardM < 0.15 ? scoreP1BShortGates(m) : hardReason;
  const p1c = p1b ? `blocked: ${p1b}` : scoreP1CGates(m);
  return {
    forwardM,
    seed: m.seed,
    hardPass: hardReason === null,
    hardReason,
    p1bShortFail: forwardM < 0.15 ? scoreP1BShortGates(m) : null,
    p1cFail: p1c,
    footAdvanceM: m.footAdvanceM,
    landingErrorM: m.landingErrorM,
    peakClearanceM: m.peakClearanceM,
    stanceSlipM: m.stanceSlipM,
    newFootSlipM: m.newFootSlipM,
    pelvisAdvanceM: m.pelvisAdvanceM,
    comAdvanceM: m.comAdvanceM,
    minPelvisHeightM: m.minPelvisHeightM,
    maxPelvisTiltDeg: m.maxPelvisTiltDeg,
    continuousSaturationMaxS: m.continuousSaturationMaxS,
    motorSaturationFraction: m.motorSaturationFraction,
    meanJointTrackingErrorRad: m.meanJointTrackingErrorRad,
    comProgressMatchErrorM: m.comProgressMatchErrorM,
    doubleSingleDouble: m.doubleSingleDouble,
    finalStabilityWindowComplete: m.finalStabilityWindowComplete,
  };
}

// ---------------------------------------------------------------------------
// V2 — demo style search (NOT certification)
// ---------------------------------------------------------------------------

/** Demo upright-completion thresholds (looser than Gate A certification). */
export const IMITATION_DEMO_HARD = {
  minPelvisHeightM: 0.9,
  maxPelvisTiltDeg: 15,
  /** Style lift target (m) for knee-led clearance reward. */
  styleLiftTargetM: 0.08,
  /** Arm hang depth band searched (STAND-relative shoulder sag, rad). */
  armHangSagMin: -0.9,
  armHangSagMax: -0.5,
  /** Absorb-knee scale band (fraction of push-12 full profile). */
  absorbScaleMax: 0.4,
  /** Reference blend per-phase cap. */
  blendMax: 0.5,
  /** Lift clearance search band (m). */
  liftClearanceMinM: 0.03,
  liftClearanceMaxM: 0.08,
} as const;

export interface ImitationStyleBreakdown {
  /** min(peakClearance, 0.08) / 0.08 — knee-led lift height. */
  readonly liftScore: number;
  /** Arms-down achieved when hang knobs set (depth + tracking proxy). */
  readonly armsScore: number;
  /** Smoothness from contSat + stance/landing residuals. */
  readonly smoothnessScore: number;
  /** Reference tracking (1 when zero-blend / null track). */
  readonly trackingScore: number;
  /** Upright residual within demo hard band (height + tilt). */
  readonly uprightScore: number;
  readonly peakClearanceM: number;
  readonly armKnobActive: boolean;
  readonly armShoulderTargetRad: number | null;
}

export interface ImitationScoreV2 {
  /** 0 = demo hard pass; 1 = fail. Lex first key. */
  readonly hardFail: 0 | 1;
  readonly hardReason: string | null;
  /** Style composite (higher better). Soft second key. */
  readonly style: number;
  readonly breakdown: ImitationStyleBreakdown;
  /** Gate A certification hard gates — informational only. */
  readonly certHardFail: 0 | 1;
  readonly certHardReason: string | null;
  readonly minPelvisHeightM: number;
  readonly maxPelvisTiltDeg: number;
  readonly peakClearanceM: number;
  readonly continuousSaturationMaxS: number | null;
  readonly newFootSlipM: number;
  readonly meanJointTrackingErrorRad: number | null;
  readonly comProgressMatchErrorM: number;
}

/**
 * Demo hard gate (NOT Gate A): full cycle + final both contacts + upright
 * (h≥0.9, tilt≤15) + zero assists. Slip/sat do not hard-fail.
 */
export function evaluateImitationDemoHard(
  m: LocomotionGateATrialMetrics,
): string | null {
  if (m.forbiddenAssistCount > 0 || m.forbiddenWorldApiCallsDuringControl > 0) {
    return 'forbidden assists';
  }
  if (m.prepareAborted) return 'prepare aborted';
  if (!m.sawPrepare || !m.sawLift || !m.sawPlace || !m.sawLoad) {
    return 'incomplete phases';
  }
  if (!m.doubleSingleDouble) return 'no double→single→double';
  if (!m.finalBothContacts) return 'final both contacts missing';
  if (m.minPelvisHeightM < IMITATION_DEMO_HARD.minPelvisHeightM) {
    return `height ${m.minPelvisHeightM.toFixed(3)} < ${IMITATION_DEMO_HARD.minPelvisHeightM}`;
  }
  if (m.maxPelvisTiltDeg > IMITATION_DEMO_HARD.maxPelvisTiltDeg) {
    return `tilt ${m.maxPelvisTiltDeg.toFixed(2)} > ${IMITATION_DEMO_HARD.maxPelvisTiltDeg}`;
  }
  return null;
}

/**
 * Style soft score for imitation round 2 (higher better).
 * Channels: knee-led lift, arms-down, smoothness, reference tracking, upright.
 */
export function scoreImitationStyleV2(
  m: LocomotionGateATrialMetrics,
  styleKnobs?: Readonly<P1ATrialStyleKnobs> | null,
): ImitationStyleBreakdown {
  const peakClearanceM = Math.max(0, m.peakClearanceM);
  const liftTarget = IMITATION_DEMO_HARD.styleLiftTargetM;
  const liftScore = Math.min(peakClearanceM, liftTarget) / liftTarget;

  const shoulder =
    typeof styleKnobs?.armShoulderSagRad === 'number' &&
    Number.isFinite(styleKnobs.armShoulderSagRad)
      ? styleKnobs.armShoulderSagRad
      : null;
  const elbow =
    typeof styleKnobs?.armElbowFlexRad === 'number' &&
    Number.isFinite(styleKnobs.armElbowFlexRad)
      ? styleKnobs.armElbowFlexRad
      : null;
  const armKnobActive =
    (shoulder !== null && Math.abs(shoulder) > 1e-4) ||
    (elbow !== null && Math.abs(elbow) > 1e-4);

  // Reward hang depth in the shallow safe band [-0.9, -0.5]; deeper/shallower
  // outside band still scores but peak is at mid-band (~-0.7).
  let armsScore = 0;
  if (armKnobActive && shoulder !== null && shoulder < 0) {
    const lo = IMITATION_DEMO_HARD.armHangSagMin;
    const hi = IMITATION_DEMO_HARD.armHangSagMax;
    const mid = 0.5 * (lo + hi);
    const half = 0.5 * (hi - lo);
    // Closeness to mid of band, plus mild reward for being negative at all.
    const band = Math.max(0, 1 - Math.abs(shoulder - mid) / Math.max(1e-6, half * 1.5));
    const depth = Math.min(1, Math.abs(shoulder) / Math.abs(lo));
    armsScore = 0.55 * band + 0.45 * depth;
  }

  const contSat = m.continuousSaturationMaxS ?? 2;
  const slip = m.newFootSlipM;
  const stance = m.stanceSlipM;
  const land = m.landingErrorM;
  // Soft penalties; contSat~0.9 baseline still gets ~0.5 smoothness.
  const smoothPen =
    contSat / 1.2 + slip / 0.15 + stance / 0.08 + land / 0.12;
  const smoothnessScore = 1 / (1 + smoothPen);

  const track = m.meanJointTrackingErrorRad;
  // When blend is zero, tracking is null — treat as neutral 0.5 so identity
  // is not auto-rewarded or punished on this channel.
  const trackingScore =
    track === null || !Number.isFinite(track)
      ? 0.5
      : 1 / (1 + track * 4);

  // Upright residual inside demo band (extra headroom rewards).
  const h = m.minPelvisHeightM;
  const tilt = m.maxPelvisTiltDeg;
  const hScore = Math.max(
    0,
    Math.min(1, (h - IMITATION_DEMO_HARD.minPelvisHeightM) / 0.12),
  );
  const tiltScore = Math.max(
    0,
    Math.min(1, 1 - tilt / IMITATION_DEMO_HARD.maxPelvisTiltDeg),
  );
  const uprightScore = 0.55 * hScore + 0.45 * tiltScore;

  return {
    liftScore,
    armsScore,
    smoothnessScore,
    trackingScore,
    uprightScore,
    peakClearanceM,
    armKnobActive,
    armShoulderTargetRad: shoulder,
  };
}

/**
 * Lexicographic v2: demo hard first, then style composite (higher better).
 * Certification Gate A is attached as info only.
 */
export function scoreImitationTrialV2(
  m: LocomotionGateATrialMetrics,
  styleKnobs?: Readonly<P1ATrialStyleKnobs> | null,
  th = LOCOMOTION_GATE_A_THRESHOLDS,
): ImitationScoreV2 {
  const hardReason = evaluateImitationDemoHard(m);
  const hardFail: 0 | 1 = hardReason === null ? 0 : 1;
  const breakdown = scoreImitationStyleV2(m, styleKnobs);
  // Weights: lift + arms + smoothness + tracking + upright ≈ [0, 5].
  const style =
    breakdown.liftScore * 1.2 +
    breakdown.armsScore * 1.0 +
    breakdown.smoothnessScore * 1.1 +
    breakdown.trackingScore * 0.9 +
    breakdown.uprightScore * 0.8;
  const certHardReason = evaluateGateATrialHardGates(m, th);
  return {
    hardFail,
    hardReason,
    style,
    breakdown,
    certHardFail: certHardReason === null ? 0 : 1,
    certHardReason,
    minPelvisHeightM: m.minPelvisHeightM,
    maxPelvisTiltDeg: m.maxPelvisTiltDeg,
    peakClearanceM: m.peakClearanceM,
    continuousSaturationMaxS: m.continuousSaturationMaxS,
    newFootSlipM: m.newFootSlipM,
    meanJointTrackingErrorRad: m.meanJointTrackingErrorRad,
    comProgressMatchErrorM: m.comProgressMatchErrorM,
  };
}

/** Lower rank is better: hardFail asc, then style desc. */
export function compareImitationScoresV2(a: ImitationScoreV2, b: ImitationScoreV2): number {
  if (a.hardFail !== b.hardFail) return a.hardFail - b.hardFail;
  return b.style - a.style;
}

/**
 * Dim layout v2 (14) — see sample modes in {@link sampleImitationCandidatesV2}.
 *   0–5  blend: prepare,lift,swing,land,load,settle
 *   6–9  shape: swingFlex,stanceBrace,spine,armSwing ∈ [0.7, 1.3]
 *  10    hang enable + depth [-0.9, -0.5] when active
 *  11    elbow flex ∈ [0, 0.35] when hang active
 *  12    liftClearanceM ∈ [0.03, 0.08]
 *  13    absorb scale ∈ [0, 0.4]
 *
 * Probe finding (push-13 baseline): any non-zero prepare/lift/swing/land/load
 * blend or arm hang collapses upright completion. Safe subspace = zero mid-
 * cycle blend + settle-only FF + absorb scale + liftClearance ≤0.065 (forward
 * floor) + shape without blend. Index 0 = identity.
 */
export const IMITATION_V2_DIMS = 14 as const;

export type ImitationV2SampleMode = 'safe' | 'micro' | 'explore';

export function unitToImitationCandidateV2(
  index: number,
  unit: readonly number[],
  mode: ImitationV2SampleMode = 'explore',
): ImitationCandidate {
  if (index === 0) {
    const blend = { ...ZERO_REFERENCE_STEP_BLEND };
    const shape = {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    };
    return { index, blend, shape, style: {} };
  }
  const u = (i: number, def = 0.5) =>
    typeof unit[i] === 'number' && Number.isFinite(unit[i]!) ? unit[i]! : def;

  // Mode caps: safe keeps mid-cycle blend at 0; micro allows ≤0.04; explore ≤0.5.
  const midMax = mode === 'safe' ? 0 : mode === 'micro' ? 0.04 : IMITATION_DEMO_HARD.blendMax;
  const settleMax = mode === 'explore' ? IMITATION_DEMO_HARD.blendMax : 0.5;
  // Forward path floors lift clearance at 0.065; values >0.065 collapse uprightness.
  const clrMax =
    mode === 'safe' || mode === 'micro'
      ? 0.065
      : IMITATION_DEMO_HARD.liftClearanceMaxM;
  const hangAllowed = mode === 'explore' || mode === 'micro';

  const blend: ReferenceStepBlendWeights = {
    prepare: mapUnit(u(0), 0, midMax * 0.5),
    lift: mapUnit(u(1), 0, midMax),
    swing: mapUnit(u(2), 0, midMax),
    land: mapUnit(u(3), 0, midMax),
    load: mapUnit(u(4), 0, midMax),
    settle: mapUnit(u(5), 0, settleMax),
  };
  const shape = {
    swingFlexScale: mapUnit(u(6), 0.7, 1.3),
    stanceBraceScale: mapUnit(u(7), 0.7, 1.3),
    spineScale: mapUnit(u(8), 0.7, 1.3),
    armSwingScale: mapUnit(u(9), 0.7, 1.3),
  };

  // Safe: never hang. Micro: rare shallow hang. Explore: original band.
  const hangU = clamp01(u(10));
  const hangThreshold = mode === 'micro' ? 0.85 : 0.35;
  const hangActive = hangAllowed && hangU >= hangThreshold;
  const hangDepth = hangActive
    ? mapUnit(
        mode === 'micro' ? hangU : (hangU - hangThreshold) / Math.max(1e-6, 1 - hangThreshold),
        mode === 'micro' ? -0.55 : IMITATION_DEMO_HARD.armHangSagMin,
        mode === 'micro' ? -0.5 : IMITATION_DEMO_HARD.armHangSagMax,
      )
    : 0;
  const elbow = hangActive ? mapUnit(u(11), 0, mode === 'micro' ? 0.2 : 0.35) : 0;
  const liftClearanceM = mapUnit(u(12), IMITATION_DEMO_HARD.liftClearanceMinM, clrMax);
  const absorbScale = mapUnit(u(13), 0, IMITATION_DEMO_HARD.absorbScaleMax);

  const hasMidBlend =
    blend.prepare > 1e-6 ||
    blend.lift > 1e-6 ||
    blend.swing > 1e-6 ||
    blend.land > 1e-6 ||
    blend.load > 1e-6 ||
    blend.settle > 1e-6;

  const style: P1ATrialStyleKnobs = {
    ...(hasMidBlend
      ? {
          referenceBlend: blend,
          // Shape only meaningful with blend.
          referenceShape: shape,
        }
      : {}),
    liftClearanceM,
    ...(absorbScale > 1e-4 ? { momentumAbsorbKneeScale: absorbScale } : {}),
    ...(hangActive
      ? {
          armShoulderSagRad: hangDepth,
          armElbowFlexRad: elbow,
        }
      : {}),
  };
  return { index, blend, shape, style };
}

/**
 * Structured presets for v2.
 * Leading entries are **safe upright** (settle-only / absorb / clearance no-ops).
 * Trailing entries are **exploratory** mid-cycle blends + hang (expected demo fail).
 */
export const IMITATION_V2_STRUCTURED_PRESETS: readonly {
  style: P1ATrialStyleKnobs;
  blend: ReferenceStepBlendWeights;
  shape: Required<ReferenceStepShapeScalars>;
  readonly note?: string;
}[] = Object.freeze([
  {
    note: 'safe: settle-only FF + low absorb (identity kinematics expected)',
    blend: {
      prepare: 0,
      lift: 0,
      swing: 0,
      land: 0,
      load: 0,
      settle: 0.25,
    },
    shape: {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    },
    style: {
      referenceBlend: {
        prepare: 0,
        lift: 0,
        swing: 0,
        land: 0,
        load: 0,
        settle: 0.25,
      },
      liftClearanceM: 0.05,
      momentumAbsorbKneeScale: 0.2,
    },
  },
  {
    note: 'safe: absorb dose only',
    blend: { ...ZERO_REFERENCE_STEP_BLEND },
    shape: {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    },
    style: {
      liftClearanceM: 0.055,
      momentumAbsorbKneeScale: 0.35,
    },
  },
  {
    note: 'safe: clearance at forward floor + settle',
    blend: {
      prepare: 0,
      lift: 0,
      swing: 0,
      land: 0,
      load: 0,
      settle: 0.4,
    },
    shape: {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    },
    style: {
      referenceBlend: {
        prepare: 0,
        lift: 0,
        swing: 0,
        land: 0,
        load: 0,
        settle: 0.4,
      },
      liftClearanceM: 0.065,
      momentumAbsorbKneeScale: 0.15,
    },
  },
  {
    note: 'explore: micro land blend (expected collapse)',
    blend: {
      prepare: 0,
      lift: 0,
      swing: 0,
      land: 0.02,
      load: 0,
      settle: 0,
    },
    shape: {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    },
    style: {
      referenceBlend: {
        prepare: 0,
        lift: 0,
        swing: 0,
        land: 0.02,
        load: 0,
        settle: 0,
      },
      liftClearanceM: 0.05,
    },
  },
  {
    note: 'explore: mild swing FF + shallow hang (expected collapse)',
    blend: {
      prepare: 0,
      lift: 0.15,
      swing: 0.2,
      land: 0.1,
      load: 0.05,
      settle: 0,
    },
    shape: {
      swingFlexScale: 1.1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    },
    style: {
      referenceBlend: {
        prepare: 0,
        lift: 0.15,
        swing: 0.2,
        land: 0.1,
        load: 0.05,
        settle: 0,
      },
      referenceShape: {
        swingFlexScale: 1.1,
        stanceBraceScale: 1,
        spineScale: 1,
        armSwingScale: 1,
      },
      liftClearanceM: 0.05,
      armShoulderSagRad: -0.6,
      armElbowFlexRad: 0.18,
      momentumAbsorbKneeScale: 0.15,
    },
  },
  {
    note: 'explore: hang only (expected collapse)',
    blend: { ...ZERO_REFERENCE_STEP_BLEND },
    shape: {
      swingFlexScale: 1,
      stanceBraceScale: 1,
      spineScale: 1,
      armSwingScale: 1,
    },
    style: {
      armShoulderSagRad: -0.7,
      armElbowFlexRad: 0.2,
      liftClearanceM: 0.05,
    },
  },
]);

/**
 * Deterministic v2 batch; index 0 = identity baseline.
 * Mix: ~50% safe / ~25% micro / ~25% explore (after structured presets).
 */
export function sampleImitationCandidatesV2(
  count: number,
  seed: number,
): ImitationCandidate[] {
  const n = Math.max(1, Math.floor(count));
  const rng = mulberry32(seed);
  const out: ImitationCandidate[] = [];
  out.push(unitToImitationCandidateV2(0, []));
  for (let p = 0; p < IMITATION_V2_STRUCTURED_PRESETS.length && out.length < n; p += 1) {
    const preset = IMITATION_V2_STRUCTURED_PRESETS[p]!;
    out.push({
      index: out.length,
      blend: preset.blend,
      shape: preset.shape,
      style: preset.style,
    });
  }
  while (out.length < n) {
    const r = rng();
    const mode: ImitationV2SampleMode =
      r < 0.5 ? 'safe' : r < 0.75 ? 'micro' : 'explore';
    const unit = Array.from({ length: IMITATION_V2_DIMS }, () => rng());
    // Always bias prepare toward zero.
    unit[0] = (unit[0] ?? 0.5) * 0.25;
    out.push(unitToImitationCandidateV2(out.length, unit, mode));
  }
  return out;
}

/**
 * Eval-only demo preset for imitation round 2.
 * Source: search candidate index 159 (sampleSeed 0, forward 0.10).
 * Demo hard PASS seeds 0–9; Gate A still fails (NOT CERTIFIED).
 * Wins vs baseline: contSat 0.92→0.20, nSlip 0.099→0.078, style 1.88→2.21.
 * Arms stay up (hang collapses uprightness). Lift height not improved.
 */
export const IMITATION_DEMO_STYLE_PRESET_V2_NOTE =
  'NOT CERTIFIED — imitation style demo only (round 2). Upright-completion hard gate, not Gate A. Best search idx 159: micro mid-cycle blend + ~0.19 absorb scale; arm hang not used.';

export const IMITATION_DEMO_STYLE_PRESET_V2_INDEX = 159 as const;

/**
 * Best upright style candidate from humanoid-imitation-search-v2 (seed 0).
 * Exact search values — do not round (reproducibility).
 */
export const IMITATION_DEMO_STYLE_PRESET_V2: Readonly<P1ATrialStyleKnobs> =
  Object.freeze({
    referenceBlend: {
      prepare: 0.000805926158791408,
      lift: 0.03858379458077252,
      swing: 0.027914157090708613,
      land: 0.003003191500902176,
      load: 0.03229048122651875,
      settle: 0.45489562419243157,
    },
    referenceShape: {
      swingFlexScale: 1.2331696103792638,
      stanceBraceScale: 1.0042451126500964,
      spineScale: 0.902751799998805,
      armSwingScale: 1.0212656793184578,
    },
    liftClearanceM: 0.04301983372541145,
    momentumAbsorbKneeScale: 0.19348429115489127,
  });
